import { styled, useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import * as d3 from 'd3';
import { memo, useEffect } from 'react';

import { collectCanvasDiagnostics, isDefined, reportThrottled } from '#shared';

import { useGeoMap } from '../GeoMapProvider';
import { EMapLayer } from '../layer.enum';

import type { ChangeEvent, TransitionEvent } from '../transitionEventEmitter';
import type { FeatureCollection } from 'geojson';

// Canvas styling configuration
const CANVAS_STYLES = {
  [EMapLayer.countries]: {
    fillStyle: '#1a237e', // palette.mapBlue[900] equivalent
    strokeStyle: 'none',
    lineWidth: 0,
  },
  [EMapLayer.tmdxServed]: {
    fillStyle: '#283593', // palette.mapBlue[700] equivalent
    strokeStyle: '#000000',
    lineWidth: 1.5,
  },
} as const;
const VALID_LAYERS = new Set([
  EMapLayer.countries,
  EMapLayer.tmdxServed,
  EMapLayer.lakes,
]);

// Watchdog sampling: 20 rows × 20 columns = 400 alpha probes.
// Reads one full-width row per sample line (20 getImageData calls total)
// and picks 20 evenly-spaced columns from each row's typed array.
const WATCHDOG_GRID = 8;
const SENTRY_THROTTLE_MS = 60_000;
const TAGS = { component: 'GlobeLayer' };

const readAlphaSum = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
  if (w <= 0 || h <= 0) {
    return 0;
  }
  let sum = 0;
  for (let row = 0; row < WATCHDOG_GRID; row++) {
    const y = Math.floor((h * (row + 1)) / (WATCHDOG_GRID + 1));
    const rowData = ctx.getImageData(0, y, w, 1).data;
    for (let col = 0; col < WATCHDOG_GRID; col++) {
      const x = Math.floor((w * (col + 1)) / (WATCHDOG_GRID + 1));
      sum += rowData[x * 4 + 3]; // alpha channel
    }
  }
  return sum;
};

const Root = styled('canvas')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  minHeight: 0,
  willChange: 'transform',
});
const GlobeLayerComponent = () => {
  const { palette } = useTheme();
  const {
    projectionRef,
    usData,
    mapRef,
    getTopography,
    onChangeProgress,
    onChangeEnd,
    transitionEventEmitter,
    topoLabels,
  } = useGeoMap();

  useEffect(() => {
    if (!(usData && mapRef.current)) {
      return;
    }

    let rafId: number | null = null;
    let hasDrawnOnce = false;
    let recoveryCount = 0;
    const canvas = mapRef.current;

    const pathGenerator = d3.geoPath(projectionRef.current);
    const animateFrame = (
      event: CustomEvent<ChangeEvent | TransitionEvent>,
    ) => {
      let topography: FeatureCollection | null;
      let renderLabels = true;
      if (!canvas) {
        console.error('❌ Canvas not found');
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('❌ Canvas context not found');
        return;
      }

      switch (event.detail.type) {
        case 'change-progress':
          const { scale } = event.detail;
          topography = getTopography(scale / 2);
          renderLabels = false;
          break;
        case 'change-end':
          // Use the final scale for rendering
          topography = getTopography(event.detail.scale);
          renderLabels = true;
          break;
        default:
          console.warn('Unknown transition event type:', event.detail.type);
          return;
      }
      try {
        const { width, height } = canvas.getBoundingClientRect();

        // Skip draw if topography data isn't available yet
        if (!topography || topography.features?.length === 0) {
          return;
        }

        // Clear canvas only after confirming we have data to draw
        ctx.clearRect(0, 0, width, height);
        topography.features.forEach(feature => {
          const layer = feature.properties?.__layer || 'unknown';
          if (!layer || !VALID_LAYERS.has(layer)) {
            console.error('Invalid layer:', layer, 'in feature:', feature);
            return; // Skip invalid features
          }
          const pathData = pathGenerator(feature);

          if (pathData) {
            // Set style based on layer
            if (layer === EMapLayer.countries) {
              ctx.fillStyle = palette.mapBlue[900];
              ctx.strokeStyle = CANVAS_STYLES.countries.strokeStyle;
              ctx.lineWidth = CANVAS_STYLES.countries.lineWidth;
            } else if (layer === EMapLayer.tmdxServed) {
              ctx.fillStyle = palette.mapBlue[700];
              ctx.strokeStyle = CANVAS_STYLES.tmdxServed.strokeStyle;
              ctx.lineWidth = CANVAS_STYLES.tmdxServed.lineWidth;
            } else {
              ctx.fillStyle = 'black';
            }

            // Draw the path
            const path2D = new Path2D(pathData);
            ctx.fill(path2D);

            if (layer === EMapLayer.tmdxServed && ctx.lineWidth > 0) {
              ctx.stroke(path2D);
            }
          }
        });

        if (renderLabels && topoLabels) {
          const labels = topoLabels
            .map(({ feature, centroid, text }) => {
              const projected = projectionRef.current(centroid);
              return {
                x: projected ? projected[0] : 0,
                y: projected ? projected[1] : 0,
                feature,
                text,
              };
            })
            .filter(label => label.text); // Only render states with abbreviations
          // Set text style
          const { width } = canvas.getBoundingClientRect();

          const minFontSize = 14;
          const maxFontSize = 22; // reduce max a bit for laptop
          const rawFontSize = width / 100; // reduce scaling rate
          const fontSize = Math.min(
            Math.max(rawFontSize, minFontSize),
            maxFontSize,
          );

          let fontWeight: number;

          if (fontSize <= 13) {
            fontWeight = 500;
          } else if (fontSize <= 16) {
            fontWeight = 600;
          } else {
            fontWeight = 700;
          }

          ctx.font = `${fontWeight} ${fontSize}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          const landStroke = palette.mapBlue[700];
          const strokeWidth = Math.max(1, Math.round(fontSize * 0.22));
          ctx.lineJoin = 'round';
          ctx.miterLimit = 2;

          labels.forEach(({ text, x, y }) => {
            ctx.strokeStyle = landStroke;
            ctx.lineWidth = strokeWidth;
            ctx.strokeText(text, x, y);
            ctx.fillText(text, x, y);
          });
        }
      } catch (error) {
        // SpecRef: ARCH-005
        // Reference: docs/decisions/ARCH-005-ui-error-detection-resilience.md#5-canvas-render-error-catch
        // Canvas render failures are captured to Sentry instead of silently discarded.
        Sentry.captureException(error, { tags: TAGS });
        console.error('❌ Error rendering map layers:', error);
      } finally {
        if (event.detail.type === 'change-end') {
          hasDrawnOnce = true;
        }
        rafId = null;
      }
    };

    const scheduleRaf = (event: CustomEvent<ChangeEvent | TransitionEvent>) => {
      if (isDefined(rafId)) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => animateFrame(event));
    };

    const unsubscribeChangeEnd = onChangeEnd(scheduleRaf);
    const unsubscribeChange = onChangeProgress(scheduleRaf);

    const reportBlankCanvas = (trigger: 'watchdog' | 'contextrestored') => {
      recoveryCount++;
      const diagnostics = collectCanvasDiagnostics();
      reportThrottled(
        trigger === 'watchdog'
          ? 'globe:blank-canvas'
          : 'globe:context-restored',
        trigger === 'watchdog'
          ? 'GlobeLayer: blank canvas detected'
          : 'GlobeLayer: canvas context restored',
        {
          throttleMs: SENTRY_THROTTLE_MS,
          level: 'warning',
          tags: { ...TAGS, trigger },
          extra: { recoveryCount },
          contexts: {
            canvases: Object.fromEntries(
              diagnostics.canvases.map((c, i) => [
                `canvas_${i}`,
                JSON.stringify(c),
              ]),
            ),
            display: { ...diagnostics.display },
            memory: { ...diagnostics.memory },
            perf: { ...diagnostics.performance },
          },
        },
      );
    };

    // Watchdog: detect externally blanked canvas (e.g. GPU context loss).
    // Skips check while a recovery redraw is already scheduled.
    const watchdogId = setInterval(() => {
      if (
        !hasDrawnOnce ||
        !canvas.width ||
        !canvas.height ||
        isDefined(rafId) ||
        document.hidden
      ) {
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }
      try {
        if (readAlphaSum(ctx, canvas.width, canvas.height) === 0) {
          reportBlankCanvas('watchdog');
          transitionEventEmitter.emit({
            type: 'change-end',
            rotation: projectionRef.current.rotate(),
            scale: projectionRef.current.scale(),
            timestamp: Date.now(),
          });
        }
      } catch {
        // if getImageData throws on edge-case canvas states; next tick retries.
      }
    }, 1_000);

    // Browser-level context loss;
    // once the browser restores it we trigger a full redraw.
    const onContextLost = (e: Event) => {
      e.preventDefault(); // signal the browser we want to restore
    };
    const onContextRestored = () => {
      reportBlankCanvas('contextrestored');
      transitionEventEmitter.emit({
        type: 'change-end',
        rotation: projectionRef.current.rotate(),
        scale: projectionRef.current.scale(),
        timestamp: Date.now(),
      });
    };
    canvas.addEventListener('contextlost', onContextLost);
    canvas.addEventListener('contextrestored', onContextRestored);

    return () => {
      if (isDefined(rafId)) {
        cancelAnimationFrame(rafId);
      }
      clearInterval(watchdogId);
      canvas.removeEventListener('contextlost', onContextLost);
      canvas.removeEventListener('contextrestored', onContextRestored);
      unsubscribeChangeEnd();
      unsubscribeChange();
    };
  }, [
    // bounds,
    getTopography,
    mapRef,
    onChangeEnd,
    onChangeProgress,
    palette.mapBlue,
    projectionRef,
    transitionEventEmitter,
    usData,
    topoLabels,
  ]);
  return <Root ref={mapRef} />;
};

export const GlobeLayer = memo(GlobeLayerComponent);
